import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { darf } from "../api";
import {
  sidebarItems, sidebarFooterItems, sichtbareEintraege, SidebarItem,
} from "../config/sidebar";
import { zielFuer } from "../lib/letzterOrt";

type Mode = "search" | "list";

type PaletteItem = {
  id: string;
  label: string;
  path: string;
  icon: ReactNode;
  /** Optional secondary descriptor (e.g. "DHBW") shown right-aligned. Empty
      when the entry stands on its own — sidebar is flat now, so most
      entries don't need it. */
  hint: string;
  color: string;
  haystack: string;
};

// Inline action shortcuts that aren't sidebar entries — query-string
// deeplinks for the common scoped views the user reaches most often.
// Saves a click vs. opening the page and toggling the filter manually.
const QUICK_ACTIONS: PaletteItem[] = [
  { id: "q.cal.dhbw",   label: "DHBW calendar",   path: "/calendar?filter=dhbw",   icon: null, hint: "DHBW",   color: "red",    haystack: "" },
  { id: "q.cal.privat", label: "Private calendar", path: "/calendar?filter=privat", icon: null, hint: "Private", color: "green",  haystack: "" },
  { id: "q.mail.dhbw",  label: "DHBW mail",        path: "/mail?view=dhbw",         icon: null, hint: "DHBW",   color: "red",    haystack: "" },
  { id: "q.mail.privat",label: "Private mail",    path: "/mail?view=privat",       icon: null, hint: "Private", color: "green",  haystack: "" },
];

function buildItems(): PaletteItem[] {
  const out: PaletteItem[] = [];
  const push = (item: SidebarItem) => {
    out.push({
      id: item.id,
      label: item.label,
      path: item.path,
      icon: item.icon,
      hint: "",
      color: item.color ?? "mauve",
      haystack: item.label.toLowerCase(),
    });
    if (item.children) for (const c of item.children) push(c);
  };
  // Auch hier nur, was man darf: die Palette hätte sonst die Namen der
  // Seiten verraten, die für jemanden gar nicht da sind.
  for (const top of sichtbareEintraege(sidebarItems, true, darf)) push(top);
  for (const f of sichtbareEintraege(sidebarFooterItems, true, darf)) push(f);
  for (const q of QUICK_ACTIONS) {
    out.push({ ...q, haystack: `${q.label} ${q.hint}`.toLowerCase() });
  }
  return out;
}

// Always returns a finite score so the result list is never empty — the
// user wanted "best matches" surfaced even on noisy/typo input. Higher is
// better; exact > prefix > substring > subsequence > partial-char overlap.
function score(item: PaletteItem, q: string): number {
  if (!q) return 1;
  const needle = q.toLowerCase();
  const label = item.label.toLowerCase();
  if (label === needle) return 10000;
  if (label.startsWith(needle)) return 5000 - label.length;
  const idx = item.haystack.indexOf(needle);
  if (idx >= 0) return 2000 - idx;

  // Subsequence + partial-match scoring. Walk the haystack, advance the
  // needle pointer on every hit, and reward long consecutive runs so
  // "cal" still ranks "Calendar" above "Lights".
  let i = 0;
  let firstIdx = -1;
  let run = 0;
  let bestRun = 0;
  for (let pos = 0; pos < item.haystack.length && i < needle.length; pos++) {
    if (item.haystack[pos] === needle[i]) {
      if (firstIdx < 0) firstIdx = pos;
      i++;
      run++;
      if (run > bestRun) bestRun = run;
    } else {
      run = 0;
    }
  }
  if (i === needle.length) {
    return 500 + bestRun * 20 - firstIdx;
  }
  // Partial char overlap — count chars from q that occur anywhere in
  // haystack (order-agnostic). Keeps unrelated items at the bottom but
  // still in the list, which is what "weniger streng" asked for.
  let overlap = 0;
  for (let j = 0; j < needle.length; j++) {
    if (item.haystack.indexOf(needle[j]) >= 0) overlap++;
  }
  return overlap;
}

type Props = {
  open: boolean;
  onClose: () => void;
};

export function CommandPalette({ open, onClose }: Props) {
  const navigate = useNavigate();
  // Dieselbe Regel wie in der Leiste: ein Sprung in einen anderen Bereich
  // landet dort, wo man zuletzt war. Siehe lib/letzterOrt.
  const jetzt = useLocation().pathname;
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState<Mode>("search");
  const [selected, setSelected] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const allItems = useMemo(() => buildItems(), []);

  const items = useMemo(() => {
    const q = query.trim();
    if (!q) return allItems;
    // Never filter — always rank all items by score. "Best matches" sit
    // at the top; weak matches stay at the bottom but remain reachable
    // so a stray keystroke doesn't blank the palette.
    const ranked = allItems
      .map((it) => ({ it, s: score(it, q) }))
      .sort((a, b) => b.s - a.s);
    return ranked.map((x) => x.it);
  }, [allItems, query]);

  // Reset every time the palette is (re-)opened.
  useEffect(() => {
    if (open) {
      setQuery("");
      setMode("search");
      setSelected(0);
      // Focus input after the open animation has had a frame to apply,
      // otherwise some browsers skip the focus ring transition.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Clamp selection when the result set shrinks.
  useEffect(() => {
    if (selected >= items.length) setSelected(Math.max(0, items.length - 1));
  }, [items.length, selected]);

  // Keep the highlighted row in view during j/k navigation.
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-idx="${selected}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [selected]);

  // Body scroll lock.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const choose = useCallback((idx: number) => {
    const it = items[idx];
    if (!it) return;
    onClose();
    navigate(zielFuer(it.path, jetzt));
  }, [items, navigate, onClose, jetzt]);

  // Global keydown handler — necessary because in "list" mode no element
  // inside the palette holds focus, so an `onKeyDown` on the overlay div
  // would never fire. We attach to window for the lifetime of the open
  // palette and rely on `mode`/`selected`/`items` via refs so the handler
  // doesn't need to be re-bound on every keystroke.
  const stateRef = useRef({ mode, selected, items });
  stateRef.current = { mode, selected, items };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      const { mode: m, selected: s, items: list } = stateRef.current;

      if (e.key === "Escape") {
        e.preventDefault();
        if (m === "search") {
          setMode("list");
          setSelected(0);
          inputRef.current?.blur();
        } else {
          onClose();
        }
        return;
      }

      if (e.key === "Enter") {
        e.preventDefault();
        const it = list[s];
        if (it) {
          onClose();
          navigate(zielFuer(it.path, jetzt));
        }
        return;
      }

      if (m === "list") {
        if (e.key === "j" || e.key === "ArrowDown") {
          e.preventDefault();
          setSelected((x) => Math.min(list.length - 1, x + 1));
        } else if (e.key === "k" || e.key === "ArrowUp") {
          e.preventDefault();
          setSelected((x) => Math.max(0, x - 1));
        } else if (e.key === "g" && !e.shiftKey) {
          e.preventDefault();
          setSelected(0);
        } else if (e.key === "G" || (e.key === "g" && e.shiftKey)) {
          e.preventDefault();
          setSelected(Math.max(0, list.length - 1));
        } else if (e.key === "i" || e.key === "/") {
          e.preventDefault();
          setMode("search");
          requestAnimationFrame(() => inputRef.current?.focus());
        } else if (e.key.length === 1 && /[a-zA-Z0-9]/.test(e.key) && !e.metaKey && !e.ctrlKey && !e.altKey) {
          // Any printable char in list mode → jump back into search and
          // start typing. Feels natural and avoids "why isn't this typing".
          e.preventDefault();
          setMode("search");
          setQuery((q) => q + e.key);
          setSelected(0);
          requestAnimationFrame(() => inputRef.current?.focus());
        }
      } else {
        // search mode — arrow keys move selection without leaving the input.
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setSelected((x) => Math.min(list.length - 1, x + 1));
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          setSelected((x) => Math.max(0, x - 1));
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, navigate, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="cmdp-overlay" onMouseDown={onClose}>
      <div
        className="cmdp-card"
        role="dialog"
        aria-modal="true"
        aria-label="Quick search"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="cmdp-search">
          <Search className="cmdp-search-icon icon-svg" />
          <input
            ref={inputRef}
            className="cmdp-input"
            placeholder="Search…   (Ctrl+K · Esc → list · j/k · Enter)"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelected(0);
              if (mode !== "search") setMode("search");
            }}
            onFocus={() => setMode("search")}
            spellCheck={false}
            autoComplete="off"
          />
          <span className={`cmdp-mode cmdp-mode-${mode}`}>
            {mode === "search" ? "INSERT" : "NORMAL"}
          </span>
        </div>

        {items.length === 0 ? (
          // Practically unreachable now that score() never filters, but
          // kept as a safety net in case the source list itself is empty.
          <div className="cmdp-empty">No matches</div>
        ) : (
          <ul className="cmdp-list" ref={listRef} role="listbox">
            {items.map((it, idx) => (
              <li
                key={it.id}
                data-idx={idx}
                role="option"
                aria-selected={idx === selected}
                className={`cmdp-row${idx === selected ? " is-selected" : ""}`}
                style={{ ["--row-color" as string]: `var(--${it.color})` }}
                onMouseEnter={() => setSelected(idx)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(idx);
                }}
              >
                <span className="cmdp-row-dot" aria-hidden />
                {it.icon && <span className="cmdp-row-icon">{it.icon}</span>}
                <span className="cmdp-row-label">{it.label}</span>
                {it.hint && <span className="cmdp-row-cat">{it.hint}</span>}
              </li>
            ))}
          </ul>
        )}

        <div className="cmdp-footer">
          <Hint k="Ctrl+K" l="open" />
          <Hint k="Esc" l={mode === "search" ? "to list" : "close"} />
          <Hint k="j / k" l="navigate" />
          <Hint k="Enter" l="go" />
          <Hint k="i  /  /" l="search" />
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Hint({ k, l }: { k: string; l: string }) {
  return (
    <span className="cmdp-hint">
      <kbd>{k}</kbd>
      <span>{l}</span>
    </span>
  );
}
